import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wrench, CheckCircle2, FileSignature } from "lucide-react";

export default function PublicEstimate() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [prefDate, setPrefDate] = useState("");
  const [prefTime, setPrefTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/estimates/${token}`);
        setData(data);
        setSignedBy(data.customer?.name || "");
        if (data.estimate.status === "approved") setDone(true);
      } catch (e) {
        setErr("This estimate isn't available.");
      }
    })();
  }, [token]);

  const approve = async (e) => {
    e.preventDefault();
    if (!signedBy.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/estimates/${token}/approve`, {
        signed_by: signedBy, preferred_date: prefDate, preferred_time: prefTime,
      });
      setDone(true);
    } catch {
      // refetch to show
    } finally { setSubmitting(false); }
  };

  if (err) return <PublicShell><div className="text-center text-slate-600">{err}</div></PublicShell>;
  if (!data) return <PublicShell><div className="text-center text-slate-500">Loading…</div></PublicShell>;

  const { estimate, customer, company } = data;

  return (
    <PublicShell>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-8">
        <div className="text-xs text-slate-500 font-bold uppercase tracking-wide">Estimate {estimate.number}</div>
        <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{estimate.title}</h1>
        <p className="text-slate-500 text-sm mt-1">From <b>{company.name}</b> · For {customer.name}</p>

        <div className="mt-5 border-t-2 border-slate-900">
          <div className="grid grid-cols-12 text-xs font-bold uppercase text-slate-500 py-2.5 tracking-wide">
            <div className="col-span-7">Description</div>
            <div className="col-span-3 text-right">Qty × Rate</div>
            <div className="col-span-2 text-right">Total</div>
          </div>
          {estimate.items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 py-2.5 text-sm border-t border-slate-100">
              <div className="col-span-7 font-medium">{it.description}</div>
              <div className="col-span-3 text-right text-slate-600">{it.quantity} × ${Number(it.unit_price).toFixed(2)}</div>
              <div className="col-span-2 text-right font-semibold">${(Number(it.quantity) * Number(it.unit_price)).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="text-slate-500 font-semibold">Estimated Total</div>
          <div className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>${(estimate.total_amount || 0).toFixed(2)}</div>
        </div>

        {estimate.notes && (
          <div className="mt-4 p-3 rounded-xl bg-slate-50 text-sm text-slate-700"><b>Notes:</b> {estimate.notes}</div>
        )}

        {done ? (
          <div className="mt-6 rounded-2xl bg-green-50 border border-green-200 p-5 text-center" data-testid="approved-block">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
            <h2 className="text-lg font-bold mt-2">Thanks, you're booked!</h2>
            <p className="text-sm text-slate-600 mt-1">
              {company.name} has been notified. They'll be in touch to confirm.
              {estimate.preferred_date && <> Your preferred slot: <b>{estimate.preferred_date}</b>{estimate.preferred_time ? <> at <b>{estimate.preferred_time}</b></> : null}.</>}
            </p>
          </div>
        ) : (
          <form onSubmit={approve} className="mt-6 space-y-4 border-t border-slate-200 pt-5">
            <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Approve this estimate</h2>
            <div>
              <Label className="text-sm font-semibold">Your name</Label>
              <Input required value={signedBy} onChange={(e) => setSignedBy(e.target.value)} className="h-12 mt-1.5 text-base" data-testid="public-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold">Preferred Date</Label>
                <Input type="date" value={prefDate} onChange={(e) => setPrefDate(e.target.value)} className="h-12 mt-1.5" data-testid="public-date" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Preferred Time</Label>
                <Input type="time" value={prefTime} onChange={(e) => setPrefTime(e.target.value)} className="h-12 mt-1.5" data-testid="public-time" />
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full h-14 bg-green-600 hover:bg-green-700 text-base font-bold rounded-xl" data-testid="public-approve">
              <FileSignature className="w-5 h-5 mr-2" />
              {submitting ? "Approving…" : "Approve & Request This Time"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              By clicking Approve you authorize {company.name} to perform the work described above for the price shown.
            </p>
          </form>
        )}
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-xl bg-blue-600 flex items-center justify-center">
            <Wrench className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>PlumberHQ</span>
        </div>
        {children}
        <p className="text-center text-xs text-slate-400 mt-6">Sent via PlumberHQ · Secure link</p>
      </div>
    </div>
  );
}
